import React, { useState } from 'react';
import { 
  MapPin, Navigation, Clock, Fuel, Receipt, 
  TrendingUp, Car, Users, ChevronRight, Play, 
  Square, Plus, FileText, Settings, Home,
  Route, Wallet, BarChart3, Menu, Bell, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const OptiTrajetDemo = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tripActive, setTripActive] = useState(false);

  // Sample data
  const stats = [
    { label: 'Trajets ce mois', value: '47', icon: Route, color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Distance totale', value: '1,842 km', icon: Navigation, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Frais carburant', value: '324 €', icon: Fuel, color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Revenus', value: '2,156 €', icon: TrendingUp, color: '#8B5CF6', bg: '#F5F3FF' },
  ];

  const recentTrips = [
    { from: 'Paris CDG', to: 'La Défense', distance: '32 km', duration: '45 min', amount: '48 €', date: 'Aujourd\'hui' },
    { from: 'Gare de Lyon', to: 'Orly', distance: '28 km', duration: '35 min', amount: '42 €', date: 'Hier' },
    { from: 'Montmartre', to: 'Versailles', distance: '25 km', duration: '40 min', amount: '38 €', date: 'Hier' },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", backgroundColor: '#F8FAFC' }} className="min-h-screen">
      
      {/* Header */}
      <header style={{ backgroundColor: '#FFFFFF' }} className="sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-200">
              <Navigation className="h-5 w-5 text-white" />
            </div>
            <span style={{ fontFamily: "'Outfit', sans-serif", color: '#0F172A' }} className="text-xl font-bold">
              OptiTrajet
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
              <Search className="h-5 w-5 text-slate-500" />
            </button>
            <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors relative">
              <Bell className="h-5 w-5 text-slate-500" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-medium">
              JD
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 border-r border-slate-200 bg-white min-h-[calc(100vh-64px)] p-4">
          <nav className="space-y-1">
            {[
              { icon: Home, label: 'Dashboard', id: 'dashboard' },
              { icon: MapPin, label: 'GPS & Navigation', id: 'gps' },
              { icon: Route, label: 'Mes trajets', id: 'trips' },
              { icon: Receipt, label: 'Dépenses', id: 'expenses' },
              { icon: FileText, label: 'Factures', id: 'invoices' },
              { icon: BarChart3, label: 'Rapports', id: 'reports' },
              { icon: Car, label: 'Flotte', id: 'fleet' },
              { icon: Settings, label: 'Paramètres', id: 'settings' },
            ].map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    isActive 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">
          
          {/* Welcome & Quick Action */}
          <div className="mb-8">
            <h1 style={{ fontFamily: "'Outfit', sans-serif", color: '#0F172A' }} className="text-2xl lg:text-3xl font-bold mb-2">
              Bonjour, Jean 👋
            </h1>
            <p style={{ color: '#64748B' }} className="text-base">
              Prêt pour la route ? Voici votre résumé du jour.
            </p>
          </div>

          {/* Start Trip CTA */}
          <Card className="mb-8 overflow-hidden border-0 shadow-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-white">
                  <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-semibold mb-1">
                    {tripActive ? 'Trajet en cours...' : 'Démarrer un trajet'}
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {tripActive ? 'Distance: 12.4 km • Durée: 18 min' : 'GPS activé • Position précise à 3m'}
                  </p>
                </div>
                <Button
                  onClick={() => setTripActive(!tripActive)}
                  size="lg"
                  className={`h-14 px-8 rounded-xl text-lg font-semibold shadow-lg transition-all ${
                    tripActive 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'bg-white hover:bg-slate-50 text-blue-700'
                  }`}
                >
                  {tripActive ? (
                    <>
                      <Square className="h-5 w-5 mr-2 fill-current" />
                      Arrêter
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5 mr-2 fill-current" />
                      Démarrer
                    </>
                  )}
                </Button>
              </div>
              
              {tripActive && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Enregistrement en cours • 48.8566, 2.3522</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <Card key={idx} className="border border-slate-100 shadow-sm hover:shadow-md transition-all">
                  <CardContent className="p-5">
                    <div 
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                      style={{ backgroundColor: stat.bg }}
                    >
                      <Icon className="h-5 w-5" style={{ color: stat.color }} />
                    </div>
                    <p style={{ color: '#64748B' }} className="text-xs font-medium mb-1">{stat.label}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", color: '#0F172A' }} className="text-2xl font-bold">
                      {stat.value}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Recent Trips */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "'Outfit', sans-serif", color: '#0F172A' }} className="text-lg font-semibold">
                Trajets récents
              </h2>
              <button style={{ color: '#2563EB' }} className="text-sm font-medium flex items-center gap-1 hover:underline">
                Voir tout <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            
            <div className="space-y-3">
              {recentTrips.map((trip, idx) => (
                <Card key={idx} className="border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Route className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p style={{ color: '#0F172A' }} className="font-medium truncate">
                          {trip.from} → {trip.to}
                        </p>
                        <p style={{ color: '#64748B' }} className="text-sm">
                          {trip.distance} • {trip.duration} • {trip.date}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p style={{ fontFamily: "'Outfit', sans-serif", color: '#10B981' }} className="font-bold text-lg">
                          {trip.amount}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", color: '#0F172A' }} className="text-lg font-semibold mb-4">
              Actions rapides
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Receipt, label: 'Scanner un reçu', color: '#F59E0B', bg: '#FFFBEB' },
                { icon: FileText, label: 'Créer une facture', color: '#8B5CF6', bg: '#F5F3FF' },
                { icon: BarChart3, label: 'Export comptable', color: '#10B981', bg: '#ECFDF5' },
                { icon: Users, label: 'Gérer la flotte', color: '#2563EB', bg: '#EFF6FF' },
              ].map((action, idx) => {
                const Icon = action.icon;
                return (
                  <Card key={idx} className="border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer">
                    <CardContent className="p-5 text-center">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                        style={{ backgroundColor: action.bg }}
                      >
                        <Icon className="h-6 w-6" style={{ color: action.color }} />
                      </div>
                      <p style={{ color: '#0F172A' }} className="text-sm font-medium">{action.label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 h-16 flex items-center justify-around z-50 px-2">
        {[
          { icon: Home, label: 'Accueil', id: 'dashboard' },
          { icon: MapPin, label: 'GPS', id: 'gps' },
          { icon: Plus, label: '', id: 'new', special: true },
          { icon: Route, label: 'Trajets', id: 'trips' },
          { icon: Wallet, label: 'Frais', id: 'expenses' },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          if (item.special) {
            return (
              <button
                key={item.id}
                onClick={() => setTripActive(!tripActive)}
                className="w-14 h-14 -mt-6 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-300 text-white"
              >
                <Plus className="h-7 w-7" />
              </button>
            );
          }
          
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center justify-center min-w-[60px] py-2 ${
                isActive ? 'text-blue-600' : 'text-slate-400'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Demo Badge */}
      <div className="fixed top-20 right-4 z-50">
        <div className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
          🎨 Démo Design OptiTrajet
        </div>
      </div>
    </div>
  );
};

export default OptiTrajetDemo;
